import React, { useCallback, useDeferredValue, useMemo, useState } from 'react'
import { Card, Form } from 'react-bootstrap'
import { ExportRow, TableExportActions, sanitizeExportFilename } from '../_table/table-export-actions'

export type AnalysisPivotRow = {
    domain: string
    page: string
    path: string
    link: string
    campaign: string
    publisher: string
    network: string
    trackedClicks: number
    untrackedClicks: number
    totalClicks: number
    pageViews: number
    transactions: number
    commission: number
    campaignClicks: number
}

type GroupKey =
    | 'page'
    | 'domain'
    | 'link'
    | 'campaign'
    | 'network'

type PivotTableProps = {
    rows: AnalysisPivotRow[]
    defaultHeight?: string
    currencyFormatter: (value: number) => string
    numberFormatter: (value: number) => string
    primaryGroupBy: GroupKey
    secondaryGroupBy: GroupKey
}

type AggregateMetrics = {
    trackedClicks: number
    untrackedClicks: number
    totalClicks: number
    pageViews: number
    transactions: number
    commission: number
    campaignClicks: number
}

const safeDivide = (value: number, total: number): number => {
    if (!total) return 0
    return value / total
}

type SecondaryGroup = {
    key: string
    label: string
    metrics: AggregateMetrics
}

type PrimaryGroup = {
    key: string
    label: string
    metrics: AggregateMetrics
    secondaryGroups: SecondaryGroup[]
}

type SortKey =
    | 'label'
    | 'pageViews'
    | 'ctr'
    | 'trackedClicks'
    | 'totalClicks'
    | 'cr'
    | 'transactions'
    | 'commission'
    | 'epc'
    | 'epv'

const GROUP_OPTIONS: { value: GroupKey; label: string }[] = [
    { value: 'page', label: 'Page' },
    { value: 'domain', label: 'Domain' },
    { value: 'link', label: 'Link' },
    { value: 'campaign', label: 'Campaign' },
    { value: 'network', label: 'Network' },
]

const GROUP_LABELS: Record<GroupKey, string> = {
    page: 'Page',
    domain: 'Domain',
    link: 'Link',
    campaign: 'Campaign',
    network: 'Network',
}

const DEFAULT_PRIMARY_LIMIT = 200
const DEFAULT_SECONDARY_LIMIT = 75

const neutralButtonStyle: React.CSSProperties = {
    border: 0,
    background: 'transparent',
    color: '#20262e',
    padding: 0,
    margin: 0,
    fontWeight: 500,
    fontSize: '0.82rem',
    lineHeight: 1.2,
}

const neutralSubtleButtonStyle: React.CSSProperties = {
    border: 0,
    background: 'transparent',
    color: '#4a5561',
    padding: 0,
    margin: 0,
    fontWeight: 500,
    fontSize: '0.82rem',
    lineHeight: 1.2,
}

const ExpandIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
    <span
        style={{
            display: 'inline-flex',
            width: '18px',
            height: '18px',
            border: '1px solid #d4dae0',
            borderRadius: '4px',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#2f3a45',
            marginRight: '8px',
            background: '#f7f9fb',
            flexShrink: 0,
        }}
        aria-hidden
    >
        <svg width="10" height="10" viewBox="0 0 12 12" style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 120ms ease-out' }}>
            <path d="M4 2L8 6L4 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </span>
)

const createEmptyMetrics = (): AggregateMetrics => ({
    trackedClicks: 0,
    untrackedClicks: 0,
    totalClicks: 0,
    pageViews: 0,
    transactions: 0,
    commission: 0,
    campaignClicks: 0,
})

const accumulateMetrics = (target: AggregateMetrics, row: AnalysisPivotRow) => {
    target.trackedClicks += row.trackedClicks || 0
    target.untrackedClicks += row.untrackedClicks || 0
    target.totalClicks += row.totalClicks || 0
    target.pageViews += row.pageViews || 0
    target.transactions += row.transactions || 0
    target.commission += row.commission || 0
    target.campaignClicks += row.campaignClicks || 0
}

const aggregateRows = (rows: AnalysisPivotRow[]): AggregateMetrics => {
    const metrics = createEmptyMetrics()
    for (const row of rows) {
        accumulateMetrics(metrics, row)
    }
    return metrics
}

const getSortValue = (label: string, metrics: AggregateMetrics, key: SortKey): number | string => {
    switch (key) {
        case 'label':
            return label
        case 'pageViews':
            return metrics.pageViews
        case 'ctr':
            return safeDivide(metrics.totalClicks, metrics.pageViews)
        case 'trackedClicks':
            return metrics.trackedClicks
        case 'totalClicks':
            return metrics.totalClicks
        case 'cr':
            return safeDivide(metrics.transactions, metrics.totalClicks)
        case 'transactions':
            return metrics.transactions
        case 'commission':
            return metrics.commission
        case 'epc':
            return safeDivide(metrics.commission, metrics.totalClicks)
        case 'epv':
            return safeDivide(metrics.commission, metrics.pageViews)
        default:
            return metrics.totalClicks
    }
}

const PivotDataTable: React.FC<PivotTableProps> = ({
    rows,
    defaultHeight = '520px',
    currencyFormatter,
    numberFormatter,
    primaryGroupBy,
    secondaryGroupBy,
}) => {
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
    const [search, setSearch] = useState('')
    const [sortKey, setSortKey] = useState<SortKey>('trackedClicks')
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
    const [primaryRenderLimit, setPrimaryRenderLimit] = useState(DEFAULT_PRIMARY_LIMIT)
    const [secondaryRenderLimits, setSecondaryRenderLimits] = useState<Record<string, number>>({})

    const deferredSearch = useDeferredValue(search)

    const hasTotalClicksColumn = !['campaign', 'network'].includes(primaryGroupBy) && !['campaign', 'network'].includes(secondaryGroupBy)

    const filteredRows = useMemo(() => {
        let result = rows
        // When campaign/network is in the grouping, bad-click-only rows (trackedClicks=0)
        // cannot be attributed to any campaign and show up as spurious "Unknown" groups.
        if (!hasTotalClicksColumn) {
            result = result.filter((row) => (row.trackedClicks || 0) > 0)
        }
        if (!deferredSearch.trim()) return result
        const query = deferredSearch.toLowerCase().trim()
        return result.filter((row) => {
            return (
                row.page.toLowerCase().includes(query) ||
                row.path.toLowerCase().includes(query) ||
                row.link.toLowerCase().includes(query) ||
                row.domain.toLowerCase().includes(query) ||
                row.campaign.toLowerCase().includes(query) ||
                row.publisher.toLowerCase().includes(query) ||
                row.network.toLowerCase().includes(query)
            )
        })
    }, [rows, deferredSearch, hasTotalClicksColumn])

    const groupedData = useMemo<PrimaryGroup[]>(() => {
        const primaryMap = new Map<string, { metrics: AggregateMetrics; secondaryMap: Map<string, AggregateMetrics> }>()

        for (const row of filteredRows) {
            const primaryValue = String(row[primaryGroupBy] || 'Unknown')
            const secondaryValue = String(row[secondaryGroupBy] || 'Unknown')

            if (!primaryMap.has(primaryValue)) {
                primaryMap.set(primaryValue, {
                    metrics: createEmptyMetrics(),
                    secondaryMap: new Map<string, AggregateMetrics>(),
                })
            }

            const primaryBucket = primaryMap.get(primaryValue)
            if (!primaryBucket) continue

            accumulateMetrics(primaryBucket.metrics, row)

            if (!primaryBucket.secondaryMap.has(secondaryValue)) {
                primaryBucket.secondaryMap.set(secondaryValue, createEmptyMetrics())
            }
            const secondaryBucket = primaryBucket.secondaryMap.get(secondaryValue)
            if (!secondaryBucket) continue
            accumulateMetrics(secondaryBucket, row)
        }

        const primaryGroups: PrimaryGroup[] = Array.from(primaryMap.entries()).map(([primaryValue, bucket]) => {
            const secondaryGroups: SecondaryGroup[] = Array.from(bucket.secondaryMap.entries()).map(
                ([secondaryValue, secondaryMetrics]) => ({
                    key: `${primaryValue}::${secondaryValue}`,
                    label: secondaryValue,
                    metrics: secondaryMetrics,
                })
            )

            return {
                key: primaryValue,
                label: primaryValue,
                metrics: bucket.metrics,
                secondaryGroups,
            }
        })

        return primaryGroups
    }, [filteredRows, primaryGroupBy, secondaryGroupBy])

    const totals = useMemo(() => aggregateRows(filteredRows), [filteredRows])

    const sortedPrimaryGroups = useMemo(() => {
        const sorted = [...groupedData]
        sorted.sort((a, b) => {
            const aValue = getSortValue(a.label, a.metrics, sortKey)
            const bValue = getSortValue(b.label, b.metrics, sortKey)

            if (typeof aValue === 'string' || typeof bValue === 'string') {
                const result = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' })
                return sortDirection === 'asc' ? result : -result
            }

            const result = aValue - bValue
            return sortDirection === 'asc' ? result : -result
        })
        return sorted
    }, [groupedData, sortDirection, sortKey])

    const visiblePrimaryGroups = useMemo(
        () => sortedPrimaryGroups.slice(0, primaryRenderLimit),
        [sortedPrimaryGroups, primaryRenderLimit]
    )

    const primaryGroupLabel = GROUP_OPTIONS.find((x) => x.value === primaryGroupBy)?.label || primaryGroupBy
    const hasPageViewColumns = primaryGroupBy === 'page' || primaryGroupBy === 'domain' || secondaryGroupBy === 'page' || secondaryGroupBy === 'domain'
    const secondaryGroupLabel = GROUP_LABELS[secondaryGroupBy]

    const toggleExpanded = (key: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else {
                next.add(key)
                setSecondaryRenderLimits((prevLimits) => ({
                    ...prevLimits,
                    [key]: prevLimits[key] || DEFAULT_SECONDARY_LIMIT,
                }))
            }
            return next
        })
    }

    const onSortClick = (key: SortKey) => {
        if (sortKey === key) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setSortKey(key)
        setSortDirection(key === 'label' ? 'asc' : 'desc')
    }

    const sortIndicator = (key: SortKey) => {
        if (sortKey !== key) return ''
        return sortDirection === 'asc' ? ' ▲' : ' ▼'
    }

    const getSortedSecondaryGroups = useCallback((group: PrimaryGroup) => {
        const sorted = [...group.secondaryGroups]
        sorted.sort((a, b) => {
            const aValue = getSortValue(a.label, a.metrics, sortKey)
            const bValue = getSortValue(b.label, b.metrics, sortKey)

            if (typeof aValue === 'string' || typeof bValue === 'string') {
                const result = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' })
                return sortDirection === 'asc' ? result : -result
            }

            const result = aValue - bValue
            return sortDirection === 'asc' ? result : -result
        })
        return sorted
    }, [sortDirection, sortKey])

    const visibleColumnCount = (hasPageViewColumns ? 10 : 7) - (hasTotalClicksColumn ? 0 : 1)

    const exportRows = useMemo<ExportRow[]>(() => {
        return visiblePrimaryGroups.flatMap((group) => {
            const groupRows: ExportRow[] = [
                {
                    Level: primaryGroupLabel,
                    [primaryGroupLabel]: group.label,
                    [secondaryGroupLabel]: '',
                    'Page views': hasPageViewColumns ? numberFormatter(group.metrics.pageViews) : '',
                    CTR: hasPageViewColumns ? `${numberFormatter(safeDivide(group.metrics.totalClicks, group.metrics.pageViews) * 100)}%` : '',
                    'Automated Clicks': numberFormatter(group.metrics.trackedClicks),
                    'Total Clicks': hasTotalClicksColumn ? numberFormatter(group.metrics.totalClicks) : '',
                    CR: `${numberFormatter(safeDivide(group.metrics.transactions, group.metrics.totalClicks) * 100)}%`,
                    Conversions: numberFormatter(group.metrics.transactions),
                    Commission: currencyFormatter(group.metrics.commission),
                    EPC: currencyFormatter(safeDivide(group.metrics.commission, group.metrics.totalClicks)),
                    EPV: hasPageViewColumns ? currencyFormatter(safeDivide(group.metrics.commission, group.metrics.pageViews)) : '',
                },
            ]

            if (!expandedGroups.has(group.key)) return groupRows

            const visibleSecondaryGroups = getSortedSecondaryGroups(group).slice(0, secondaryRenderLimits[group.key] || DEFAULT_SECONDARY_LIMIT)

            const secondaryRows = visibleSecondaryGroups.map<ExportRow>((sub) => ({
                Level: secondaryGroupLabel,
                [primaryGroupLabel]: group.label,
                [secondaryGroupLabel]: sub.label,
                'Page views': hasPageViewColumns ? numberFormatter(sub.metrics.pageViews) : '',
                CTR: hasPageViewColumns ? `${numberFormatter(safeDivide(sub.metrics.totalClicks, sub.metrics.pageViews) * 100)}%` : '',
                'Automated Clicks': numberFormatter(sub.metrics.trackedClicks),
                'Total Clicks': hasTotalClicksColumn ? numberFormatter(sub.metrics.totalClicks) : '',
                CR: `${numberFormatter(safeDivide(sub.metrics.transactions, sub.metrics.totalClicks) * 100)}%`,
                Conversions: numberFormatter(sub.metrics.transactions),
                Commission: currencyFormatter(sub.metrics.commission),
                EPC: currencyFormatter(safeDivide(sub.metrics.commission, sub.metrics.totalClicks)),
                EPV: hasPageViewColumns ? currencyFormatter(safeDivide(sub.metrics.commission, sub.metrics.pageViews)) : '',
            }))

            return [...groupRows, ...secondaryRows]
        })
    }, [
        visiblePrimaryGroups,
        primaryGroupLabel,
        secondaryGroupLabel,
        hasPageViewColumns,
        hasTotalClicksColumn,
        numberFormatter,
        currencyFormatter,
        expandedGroups,
        secondaryRenderLimits,
        getSortedSecondaryGroups,
    ])

    const exportFilename = useMemo(
        () => sanitizeExportFilename(`link-performance-${primaryGroupBy}-${secondaryGroupBy}`),
        [primaryGroupBy, secondaryGroupBy]
    )

    const renderGroupLabel = (label: string, indent = false) => {
        return (
            <span
                title={label}
                style={{
                    maxWidth: indent ? '320px' : '360px',
                    display: 'inline-block',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    textAlign: 'left',
                    lineHeight: 1.35,
                    verticalAlign: 'bottom',
                }}
            >
                {label}
            </span>
        )
    }

    return (
        <Card
            style={{
                maxWidth: '100%',
                overflow: 'hidden',
                border: '1px solid #dde3ea',
                boxShadow: '0 6px 18px rgba(20, 29, 38, 0.05)',
            }}
        >
            <Card.Body>
                <div className="d-flex flex-column flex-lg-row gap-3 justify-content-between align-items-lg-start mb-3">
                    <Form.Group className="flex-grow-1" style={{ minWidth: '220px', maxWidth: '360px' }}>
                        <Form.Label className="mb-1">Search</Form.Label>
                        <Form.Control
                            size="sm"
                            placeholder="Filter rows by page, link, campaign..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value)
                                setPrimaryRenderLimit(DEFAULT_PRIMARY_LIMIT)
                            }}
                        />
                    </Form.Group>

                    <TableExportActions
                        data={exportRows}
                        filename={exportFilename}
                        className="d-flex flex-wrap gap-2 justify-content-lg-end"
                        disabled={exportRows.length === 0}
                    />
                </div>

                <div className="small text-muted mb-3">
                    Totals: {numberFormatter(hasTotalClicksColumn ? totals.totalClicks : totals.trackedClicks)} clicks, {numberFormatter(totals.transactions)} conversions,{' '}
                    {currencyFormatter(totals.commission)} commission
                    {hasPageViewColumns ? `, ${numberFormatter(totals.pageViews)} page views` : ''}
                </div>

                <div
                    className="table-responsive"
                    style={{ maxHeight: defaultHeight, maxWidth: '100%', width: '100%', overflow: 'auto' }}
                >
                    <table
                        className="table table-sm align-middle mb-0"
                        style={{ tableLayout: 'fixed', width: '100%', fontSize: '0.82rem' }}
                    >
                        <thead className="sticky-top" style={{ backgroundColor: '#f1f4f7' }}>
                            <tr>
                                <th style={{ width: '32%', textAlign: 'left', fontWeight: 600, paddingTop: '0.35rem', paddingBottom: '0.35rem' }}>
                                    <button type="button" style={{ ...neutralButtonStyle, textAlign: 'left' }} onClick={() => onSortClick('label')}>
                                        {primaryGroupLabel}{sortIndicator('label')}
                                    </button>
                                </th>
                                {hasPageViewColumns && (
                                    <th style={{ width: '10%', fontWeight: 600, paddingTop: '0.35rem', paddingBottom: '0.35rem' }}>
                                        <button type="button" style={neutralButtonStyle} onClick={() => onSortClick('pageViews')}>
                                            Page views{sortIndicator('pageViews')}
                                        </button>
                                    </th>
                                )}
                                {hasPageViewColumns && (
                                    <th style={{ width: '7%', fontWeight: 600, paddingTop: '0.35rem', paddingBottom: '0.35rem' }}>
                                        <button type="button" style={neutralButtonStyle} onClick={() => onSortClick('ctr')}>
                                            CTR{sortIndicator('ctr')}
                                        </button>
                                    </th>
                                )}
                                <th style={{ width: '13%', fontWeight: 600, paddingTop: '0.35rem', paddingBottom: '0.35rem' }}>
                                    <button type="button" style={neutralButtonStyle} onClick={() => onSortClick('trackedClicks')}>
                                        Automated Clicks{sortIndicator('trackedClicks')}
                                    </button>
                                </th>
                                {hasTotalClicksColumn && (
                                <th style={{ width: '10%', fontWeight: 600, paddingTop: '0.35rem', paddingBottom: '0.35rem' }}>
                                    <button type="button" style={neutralButtonStyle} onClick={() => onSortClick('totalClicks')}>
                                        Total Clicks{sortIndicator('totalClicks')}
                                    </button>
                                </th>
                                )}
                                <th style={{ width: '7%', fontWeight: 600, paddingTop: '0.35rem', paddingBottom: '0.35rem' }}>
                                    <button type="button" style={neutralButtonStyle} onClick={() => onSortClick('cr')}>
                                        CR{sortIndicator('cr')}
                                    </button>
                                </th>
                                <th style={{ width: '10%', fontWeight: 600, paddingTop: '0.35rem', paddingBottom: '0.35rem' }}>
                                    <button type="button" style={neutralButtonStyle} onClick={() => onSortClick('transactions')}>
                                        Conversions{sortIndicator('transactions')}
                                    </button>
                                </th>
                                <th style={{ width: '11%', fontWeight: 600, paddingTop: '0.35rem', paddingBottom: '0.35rem' }}>
                                    <button type="button" style={neutralButtonStyle} onClick={() => onSortClick('commission')}>
                                        Commission{sortIndicator('commission')}
                                    </button>
                                </th>
                                <th style={{ width: '8%', fontWeight: 600, paddingTop: '0.35rem', paddingBottom: '0.35rem' }}>
                                    <button type="button" style={neutralButtonStyle} onClick={() => onSortClick('epc')}>
                                        EPC{sortIndicator('epc')}
                                    </button>
                                </th>
                                {hasPageViewColumns && (
                                    <th style={{ width: '8%', fontWeight: 600, paddingTop: '0.35rem', paddingBottom: '0.35rem' }}>
                                        <button type="button" style={neutralButtonStyle} onClick={() => onSortClick('epv')}>
                                            EPV{sortIndicator('epv')}
                                        </button>
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {visiblePrimaryGroups.map((group) => {
                                const isExpanded = expandedGroups.has(group.key)
                                const sortedSecondaryGroups = getSortedSecondaryGroups(group)
                                const secondaryLimit = secondaryRenderLimits[group.key] || DEFAULT_SECONDARY_LIMIT
                                const visibleSecondaryGroups = sortedSecondaryGroups.slice(0, secondaryLimit)
                                const hasMoreSecondary = sortedSecondaryGroups.length > secondaryLimit

                                return (
                                    <React.Fragment key={group.key}>
                                        <tr style={{ backgroundColor: '#f5f7fa' }}>
                                            <td style={{ textAlign: 'left' }}>
                                                <button
                                                    type="button"
                                                    style={{
                                                        ...neutralButtonStyle,
                                                        display: 'inline-flex',
                                                        alignItems: 'flex-start',
                                                        textAlign: 'left',
                                                    }}
                                                    onClick={() => toggleExpanded(group.key)}
                                                >
                                                    <ExpandIcon expanded={isExpanded} />
                                                    {renderGroupLabel(group.label)}
                                                </button>
                                            </td>
                                            {hasPageViewColumns && <td>{numberFormatter(group.metrics.pageViews)}</td>}
                                            {hasPageViewColumns && <td>{`${numberFormatter(safeDivide(group.metrics.totalClicks, group.metrics.pageViews) * 100)}%`}</td>}
                                            <td>{numberFormatter(group.metrics.trackedClicks)}</td>
                                            {hasTotalClicksColumn && <td>{numberFormatter(group.metrics.totalClicks)}</td>}
                                            <td>{`${numberFormatter(safeDivide(group.metrics.transactions, group.metrics.totalClicks) * 100)}%`}</td>
                                            <td>{numberFormatter(group.metrics.transactions)}</td>
                                            <td>{currencyFormatter(group.metrics.commission)}</td>
                                            <td>{currencyFormatter(safeDivide(group.metrics.commission, group.metrics.totalClicks))}</td>
                                            {hasPageViewColumns && <td>{currencyFormatter(safeDivide(group.metrics.commission, group.metrics.pageViews))}</td>}
                                        </tr>

                                        {isExpanded &&
                                            visibleSecondaryGroups.map((sub) => (
                                                <tr key={sub.key}>
                                                    <td className="ps-4 text-muted" style={{ textAlign: 'left' }}>
                                                        {renderGroupLabel(sub.label, true)}
                                                    </td>
                                                    {hasPageViewColumns && <td>{numberFormatter(sub.metrics.pageViews)}</td>}
                                                    {hasPageViewColumns && <td>{`${numberFormatter(safeDivide(sub.metrics.totalClicks, sub.metrics.pageViews) * 100)}%`}</td>}
                                                    <td>{numberFormatter(sub.metrics.trackedClicks)}</td>
                                                    {hasTotalClicksColumn && <td>{numberFormatter(sub.metrics.totalClicks)}</td>}
                                                    <td>{`${numberFormatter(safeDivide(sub.metrics.transactions, sub.metrics.totalClicks) * 100)}%`}</td>
                                                    <td>{numberFormatter(sub.metrics.transactions)}</td>
                                                    <td>{currencyFormatter(sub.metrics.commission)}</td>
                                                    <td>{currencyFormatter(safeDivide(sub.metrics.commission, sub.metrics.totalClicks))}</td>
                                                    {hasPageViewColumns && <td>{currencyFormatter(safeDivide(sub.metrics.commission, sub.metrics.pageViews))}</td>}
                                                </tr>
                                            ))}

                                        {isExpanded && hasMoreSecondary && (
                                            <tr>
                                                <td colSpan={visibleColumnCount} className="ps-4">
                                                    <button
                                                        type="button"
                                                        style={neutralSubtleButtonStyle}
                                                        onClick={() => {
                                                            setSecondaryRenderLimits((prev) => ({
                                                                ...prev,
                                                                [group.key]: (prev[group.key] || DEFAULT_SECONDARY_LIMIT) + DEFAULT_SECONDARY_LIMIT,
                                                            }))
                                                        }}
                                                    >
                                                        Show more secondary rows ({sortedSecondaryGroups.length - secondaryLimit} remaining)
                                                    </button>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })}

                            {sortedPrimaryGroups.length > primaryRenderLimit && (
                                <tr>
                                    <td colSpan={visibleColumnCount}>
                                        <button
                                            type="button"
                                            style={neutralSubtleButtonStyle}
                                            onClick={() => setPrimaryRenderLimit((prev) => prev + DEFAULT_PRIMARY_LIMIT)}
                                        >
                                            Show more groups ({sortedPrimaryGroups.length - primaryRenderLimit} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card.Body>
        </Card>
    )
}

export default PivotDataTable
